import { Injectable } from '@angular/core';
import { TypedArray } from '@shared/data';

import { IViewsVectors } from '@viewer/cornerstone.data';

@Injectable()
export class PixelDataTransformationService {
  // function which normalizes a vector
  public normalizeVector(vector) {
    const norm = Math.sqrt(
      vector[0] * vector[0] + vector[1] * vector[1] + vector[2] * vector[2]
    );
    return [vector[0] / norm, vector[1] / norm, vector[2] / norm];
  }

  // function which returns the index of the maximum absolute value in each subarray
  public argMaxAbs(arr: number[][]): number[] {
    const maxIdx: number[] = [];
    for (let i = 0; i < arr.length; i++) {
      const row = arr[i];
      const maxAbsVal = row.reduce(
        (acc, val) => Math.max(acc, Math.abs(val)),
        -Infinity
      );
      const maxAbsIdx = row.findIndex((val) => Math.abs(val) === maxAbsVal);
      maxIdx.push(maxAbsIdx!);
    }
    return maxIdx;
  }

  public reorder(arr: number[], order: number[]): number[] {
    const reordered = arr.slice();
    for (let i = 0; i < arr.length; i++) {
      reordered[i] = arr[order[i]];
    }
    return reordered;
  }

  // Returns the 1D index in a 2D slice for a given point and shape.
  getIdxInSlice(point: number[], shape: number[]): number {
    const [col, row] = point;
    const [col_size, row_size] = shape;
    return row * col_size + col;
  }

  // Returns the 1D index in a 3D array for a given point and shape.
  getIdxInArray(point: number[], shape: number[]): number {
    const [col, row, slice] = point;
    const [col_size, row_size] = shape;
    return slice * row_size * col_size + row * col_size + col;
  }

  // Returns the point in a 2D slice for a given 1D index and shape.
  getPointInSlice(idx: number, shape: number[]): number[] {
    const [col_size, row_size] = shape;
    const row = Math.floor(idx / col_size);
    const col = idx - row * col_size;
    return [col, row];
  }

  // Returns the point in a 3D array for a given 1D index and shape.
  getPointInArray(idx: number, shape: number[]): number[] {
    const [col_size, row_size] = shape;
    const slice = Math.floor(idx / (row_size * col_size));
    const row = Math.floor((idx - slice * row_size * col_size) / col_size);
    const col = idx - slice * row_size * col_size - row * col_size;
    return [col, row, slice];
  }

  // Transposes a 3D point in a given shape to a new point in the same 3D space but with a different order and signs of the axes.
  public transposePoint(
    point: number[],
    shape: number[],
    order: number[],
    signs: number[]
  ): number[] {
    const [col, row, slice] = point;
    const old_idxes = [col, row, slice];
    const new_idxes = old_idxes.map((idx, i) => old_idxes[order[i]]);
    for (let i = 0; i < 3; i++) {
      if (signs[i] === -1) {
        new_idxes[i] = shape[order[i]] - 1 - new_idxes[i];
      }
    }
    return new_idxes;
  }

  // Define a function that transposes the volume data based on a specified shape, order and signs.
  public transpose(
    arr: TypedArray,
    shape: number[],
    order: number[],
    signs: number[]
  ): TypedArray {
    const transposed = arr.slice(); // Copy the input array.

    const new_shape = shape.map((val, i) => shape[order[i]]); // Get the new shape of the volume.

    const [col_size, row_size, slice_size] = shape; // Destructure the shape of the volume.
    for (let slice = 0; slice < slice_size; slice++) {
      for (let row = 0; row < row_size; row++) {
        for (let col = 0; col < col_size; col++) {
          const old_idxes = [col, row, slice];
          const new_idxes = this.transposePoint(old_idxes, shape, order, signs);

          const old_idx = this.getIdxInArray(old_idxes, shape);
          const new_idx = this.getIdxInArray(new_idxes, new_shape);
          transposed[new_idx] = arr[old_idx];
        }
      }
    }
    return transposed;
  }

  public getSpaceDirections(axialSpaceDirections: number[][]): {
    axial: number[][];
    coronal: number[][];
    sagittal: number[][];
  } {
    const [rowDir, colDir, frameDir] = axialSpaceDirections;
    return {
      axial: [rowDir, colDir, frameDir],
      coronal: [frameDir, colDir, rowDir],
      sagittal: [frameDir, rowDir, colDir],
    };
  }

  // Takes two arrays containing a transformation order and signs and returns a new array with the order and signs rearranged according to the new order.
  public getTransformOrder(
    oldOrder: [order: number[], signs: number[]],
    newOrder: [order: number[], signs: number[]]
  ): [order: number[], signs: number[]] {
    const [oldOrderIdx, oldOrderSigns] = oldOrder;
    const [newOrderIdx, newOrderSigns] = newOrder;
    const newReordered = [0, 0, 0];
    const newReorderedSigns = [0, 0, 0];
    for (let i = 0; i < 3; i++) {
      newReordered[i] = oldOrderIdx.indexOf(newOrderIdx[i]);
      newReorderedSigns[i] = newOrderSigns[i] * oldOrderSigns[newReordered[i]];
    }
    return [newReordered, newReorderedSigns];
  }

  public getOrdersAndSigns(): IViewsVectors {
    return {
      axial: {
        order: [0, 1, 2],
        signs: [1, 1, 1],
      },
      coronal: {
        order: [0, 2, 1],
        signs: [1, -1, 1],
      },
      sagittal: {
        order: [1, 2, 0],
        signs: [1, -1, -1],
      },
    };
  }
}
